<template>
  <div class="image-content">
    <div class="full-width-container">
      <!-- 图像生成列表 -->
      <div class="image-list">
        <!-- 新生成的图片（优先显示） -->
        <div class="image-item" v-for="(item, index) in newImageItems" :key="'new-' + index">
          <!-- 模型名称和时间 -->
          <div class="image-header">
            <div class="image-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.resolution }}
              </div>
              <div class="type-badge">
                {{ item.type === 'text-to-image' ? '文生图' : '图生图' }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 图生图布局 -->
          <div v-if="item.type === 'image-to-image'" class="content-layout">
            <!-- 左侧：上传图片 -->
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <img 
                    :src="item.referenceImage" 
                    alt="参考图片" 
                    class="preview-image"
                  >
                </div>
              </div>
            </div>
            
            <!-- 中间箭头 -->
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <!-- 右侧：生成的图片 -->
            <div class="image-result-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <!-- 生成中状态 - 统一加载样式 -->
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成图片...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text">{{ Math.round(item.progress || 0) }}%</span>
                      </div>
                    </div>
                  </div>
                  <!-- 生成完成状态 -->
                  <img 
                    v-else 
                    :src="item.imageUrl" 
                    :alt="item.prompt" 
                    class="result-image"
                  >
                </div>
              </div>
            </div>
          </div>
          
          <!-- 文生图布局 -->
          <div v-else-if="item.type === 'text-to-image'" class="image-content-layout">
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <!-- 生成中状态 - 统一加载样式 -->
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成图片...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text">{{ Math.round(item.progress || 0) }}%</span>
                      </div>
                    </div>
                  </div>
                  <!-- 生成完成状态 -->
                  <img 
                    v-else 
                    :src="item.imageUrl" 
                    :alt="item.prompt" 
                    class="result-image"
                  >
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyPrompt(item.prompt)" title="复制提示词">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateImage(item)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editImage(item)" title="重新编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn" @click="downloadImage(item)" title="下载">
                <i class="icon">⬇️</i>
              </button>
              <button class="action-btn delete" @click="deleteImage(index, true)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 历史生成的图片（后置加载） -->
        <div class="image-item" v-for="(item, index) in historyImageItems" :key="'history-' + item.id">
          <!-- 模型名称和时间 -->
          <div class="image-header">
            <div class="image-info-badges">
              <div class="model-badge">
                <i class="icon">⚙️</i> {{ item.model }}
              </div>
              <div class="info-badge">
                {{ item.resolution }}
              </div>
              <div class="type-badge">
                {{ item.type === 'text-to-image' ? '文生图' : '图生图' }}
              </div>
            </div>
            <div class="generate-time">
              {{ item.generateTime }}
            </div>
          </div>
          
          <!-- 图生图布局 -->
          <div v-if="item.type === 'image-to-image'" class="content-layout">
            <!-- 左侧：上传图片 -->
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <img 
                    :src="item.referenceImage" 
                    alt="参考图片" 
                    class="preview-image"
                  >
                </div>
              </div>
            </div>
            
            <!-- 中间箭头 -->
            <div class="arrow-section">
              <div class="arrow">→</div>
            </div>
            
            <!-- 右侧：生成的图片 -->
            <div class="image-result-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <!-- 生成中状态 - 统一加载样式 -->
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成图片...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text">{{ Math.round(item.progress || 0) }}%</span>
                      </div>
                    </div>
                  </div>
                  <!-- 生成完成状态 -->
                  <img 
                    v-else 
                    :src="item.imageUrl" 
                    :alt="item.prompt" 
                    class="result-image"
                  >
                </div>
              </div>
            </div>
          </div>
          
          <!-- 文生图布局 -->
          <div v-else-if="item.type === 'text-to-image'" class="image-content-layout">
            <div class="image-section">
              <div class="media-box" :class="getImageClass(item.resolution)">
                <div class="image-container">
                  <!-- 生成中状态 - 统一加载样式 -->
                  <div v-if="item.status === 'generating'" class="loading-container">
                    <div class="glass-effect">
                      <div class="glass-shine"></div>
                    </div>
                    <div class="loading-content">
                      <div class="loading-spinner"></div>
                      <div class="loading-text">正在生成图片...</div>
                      <div class="progress-bar-container">
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: (item.progress || 0) + '%' }"></div>
                        </div>
                        <span class="progress-text">{{ Math.round(item.progress || 0) }}%</span>
                      </div>
                    </div>
                  </div>
                  <!-- 生成完成状态 -->
                  <img 
                    v-else 
                    :src="item.imageUrl" 
                    :alt="item.prompt" 
                    class="result-image"
                  >
                </div>
              </div>
            </div>
          </div>
          
          <!-- 提示词和功能按钮区域 -->
          <div class="prompt-container">
            <div class="prompt-content">
              <div class="prompt">{{ item.prompt }}</div>
            </div>
            <div class="action-buttons">
              <button class="action-btn" @click="copyPrompt(item.prompt)" title="复制提示词">
                <i class="icon">📋</i>
              </button>
              <button class="action-btn" @click="regenerateImage(item)" title="重新生成">
                <i class="icon">🔄</i>
              </button>
              <button class="action-btn" @click="editImage(item)" title="重新编辑">
                <i class="icon">✏️</i>
              </button>
              <button class="action-btn" @click="downloadImage(item)" title="下载">
                <i class="icon">⬇️</i>
              </button>
              <button class="action-btn delete" @click="deleteImage(index, false)" title="删除">
                <i class="icon">🗑️</i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <div v-if="newImageItems.length === 0 && historyImageItems.length === 0 && !isLoadingHistory" class="empty-state">
          <div class="empty-icon">🖼️</div>
          <h3>还没有生成过图片</h3>
          <p>使用下面的工具栏开始生成您的第一张图片吧！</p>
        </div>

        <!-- 广告位 -->
        <AdBanner position="image-generate" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import historyCache from '@/utils/historyCache'
import AdBanner from '@/components/AdBanner.vue'

export default {
  name: 'ImageContent',
  components: {
    AdBanner
  },
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      newImageItems: [],
      historyImageItems: [],
      isLoadingHistory: false,
      isLoaded: false
    }
  },
  mounted() {
    this.loadHistory()
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          this.generateFromParams(newParams)
        }
      },
      deep: true
    }
  },
  methods: {
    async loadHistory() {
      if (this.isLoaded) return
      this.isLoaded = true
      
      // 延迟加载历史数据，让新生成的内容优先显示
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 始终从数据库加载最新数据，确保新生成的图片不会丢失
      // 缓存仅用于加速切换时的显示，不替代数据库查询
      this.isLoadingHistory = true
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const user_id = userInfo.id || 0
      
      try {
        const response = await axios.get('/api/image-history', {
          params: { user_id: user_id }
        })
        
        console.log('=== 收到图片历史数据 ===')
        console.log('原始响应:', response.data)
        
        // 处理响应数据 - 可能是直接数组或包装在响应中
        let data = response.data
        if (data && data.data) {
          data = data.data
        }
        
        console.log('处理后的数据:', data)
        
        // 确保 data 是数组
        if (!Array.isArray(data)) {
          console.log('警告: data 不是数组，设置为空数组')
          data = []
        }
        
        console.log('准备处理的记录数量:', data.length)
        
        // 更新缓存（仅用于加速下次加载，但下次仍会从数据库获取最新数据）
        historyCache.updateModuleHistory('image', data)
        
        // 将后端数据格式转换为前端期望的格式，存入历史列表
        this.historyImageItems = this.formatHistoryData(data)
        
        console.log('处理后的 historyImageItems:', this.historyImageItems)
        
      } catch (error) {
        console.error('加载图片历史失败:', error)
        // 数据库加载失败时才使用缓存作为降级
        if (historyCache.isCacheValid() && historyCache.hasModuleData('image')) {
          console.log('数据库加载失败，使用缓存作为降级')
          const cachedData = historyCache.getModuleHistory('image')
          this.historyImageItems = this.formatHistoryData(cachedData)
        } else {
          this.historyImageItems = []
        }
      } finally {
        this.isLoadingHistory = false
      }
    },
    
    formatHistoryData(data) {
      // 自动修复图片URL中的端口问题（从8080改为5000）
      const fixImageUrl = (url) => {
        if (!url) return ''
        return url.replace('localhost:8080', 'localhost:5000')
      }
      
      return data.map((item, index) => {
        const mappedItem = {
          id: item.id,
          type: item.referenceImage ? 'image-to-image' : 'text-to-image',
          prompt: item.prompt || '',
          model: item.model || '',
          resolution: item.size || item.aspectRatio || '',
          status: 'completed',
          referenceImage: fixImageUrl(item.referenceImage),
          imageUrl: fixImageUrl(item.imageUrl),
          generateTime: item.createdAt || item.created_at || ''
        }
        
        console.log(`处理记录 ${index + 1}:`, {
          id: item.id,
          originalReferenceImage: item.referenceImage,
          fixedReferenceImage: mappedItem.referenceImage,
          originalImageUrl: item.imageUrl,
          fixedImageUrl: mappedItem.imageUrl
        })
        
        return mappedItem
      })
    },
    
    generateFromParams(params) {
      console.log('=== generateFromParams 接收参数 ===')
      console.log('params:', params)
      
      // 只是接收参数，不主动生成，让用户在工具栏中点击生成按钮
      // 参数会自动传递给 ImageToolbar，用户手动触发生成
    },
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    getImageClass(resolution) {
      if (!resolution) return {}
      
      const match = resolution.match(/(\d+)x(\d+)/)
      if (!match) return {}
      
      const width = parseInt(match[1])
      const height = parseInt(match[2])
      const ratio = width / height
      
      if (ratio > 1.7) {
        return { 'aspect-16-9': true }
      } else if (ratio < 0.6) {
        return { 'aspect-9-16': true }
      } else {
        return { 'aspect-1-1': true }
      }
    },
    copyPrompt(prompt) {
      navigator.clipboard.writeText(prompt).then(() => {
        alert('提示词已复制到剪贴板')
      })
    },
    regenerateImage(item) {
      console.log('重新生成:', item)
    },
    editImage(item) {
      console.log('编辑:', item)
    },
    downloadImage(item) {
      console.log('下载:', item)
    },
    async deleteImage(index, isNew) {
      if (!confirm('确定要删除这张图片吗？')) {
        return
      }
      
      try {
        if (isNew) {
          this.newImageItems.splice(index, 1)
          console.log('✅ 已删除新生成的图片')
        } else {
          const item = this.historyImageItems[index]
          console.log('🗑️ 准备删除图片:', item)
          
          // 检查是否有有效的ID
          if (item.id && item.id > 0) {
            const response = await fetch(`/api/image-history/${item.id}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            })
            
            const data = await response.json()
            console.log('📤 删除响应:', data)
            
            // 检查删除是否成功（后端返回 success 或 code）
            if (data.success === true || data.code === 200) {
              console.log('✅ 数据库删除成功')
            } else if (data.success === false || data.code === 404) {
              console.warn('⚠️ 记录不存在，可能已被删除')
            } else {
              console.warn('⚠️ 数据库删除失败，但继续移除前端显示')
            }
          } else {
            console.warn('⚠️ 没有有效的ID，跳过数据库删除')
          }
          
          // 无论如何都从前端移除
          this.historyImageItems.splice(index, 1)
          console.log('✅ 前端列表已更新')
        }
      } catch (error) {
        console.error('❌ 删除失败:', error)
        alert('删除失败，请重试')
      }
    },
    
    addNewImage(newImage) {
      // 确保新添加的图片有type字段
      console.log('=== ImageContent 接收到的图片 ===')
      console.log('newImage:', newImage)
      
      const imageWithType = {
        type: newImage.type || 'text-to-image',
        status: newImage.status || 'completed',
        progress: newImage.progress || 0,
        ...newImage
      }
      
      console.log('imageWithType:', imageWithType)
      this.newImageItems.unshift(imageWithType)
      
      // 返回索引，方便后续更新
      return 0
    },
    
    updateImageProgress(index, progress) {
      if (this.newImageItems[index]) {
        this.$set(this.newImageItems[index], 'progress', progress)
      }
    },
    
    updateImageResult(index, imageUrl) {
      if (this.newImageItems[index]) {
        console.log('=== ImageContent: updateImageResult ===')
        console.log('index:', index)
        console.log('before:', this.newImageItems[index])
        
        this.$set(this.newImageItems[index], 'status', 'completed')
        this.$set(this.newImageItems[index], 'progress', 100)
        this.$set(this.newImageItems[index], 'imageUrl', imageUrl)
        console.log('图片生成成功')
        
        console.log('after:', this.newImageItems[index])
        console.log('newImageItems:', this.newImageItems)
      }
    }
  }
}
</script>

<style scoped>
.image-content {
  width: 100%;
}

.full-width-container {
  width: 100%;
  box-sizing: border-box;
  max-width: 1600px;
  margin: 0 auto;
  padding-right: 50px;
  overflow: hidden;
}

/* 图片列表 */
.image-list {
  width: 100%;
  margin-bottom: 120px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-item {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-radius: 16px;
  padding: 16px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  height: 66.67vh;
  min-height: 500px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.image-item:hover {
  box-shadow: 0 8px 32px rgba(0, 230, 172, 0.1);
}

.image-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
}

.image-info-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.model-badge {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-badge {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #e6e6e6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.type-badge {
  background: rgba(0, 230, 172, 0.15);
  border: 1px solid rgba(0, 230, 172, 0.4);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #00e6ac;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.generate-time {
  font-size: 12px;
  color: #aaa;
  white-space: nowrap;
  margin-left: 10px;
}

/* 图生图布局 */
.content-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
  gap: 20px;
}

.arrow-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  flex-shrink: 0;
}

.arrow {
  font-size: 24px;
  color: #00e6ac;
  font-weight: bold;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.6; }
}

.image-result-section {
  flex: 1.2;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.glass-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
  border-radius: 12px;
  z-index: 1;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

/* 文生图布局 */
.image-content-layout {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.image-section {
  flex: 1;
  max-width: 60%;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.media-box {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.02);
  border: none;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
}

/* 9:16竖图 - 居中显示，上下留边距 */
.media-box.aspect-9-16 {
  padding: 20px 0;
}

.media-box.aspect-9-16 .image-container,
.media-box.aspect-9-16 .result-image {
  height: 100%;
  width: auto;
  max-width: 100%;
}

/* 16:9横图 - 保持原始比例 */
.media-box.aspect-16-9 .image-container,
.media-box.aspect-16-9 .result-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 1:1正方形 - 保持原始比例 */
.media-box.aspect-1-1 .image-container,
.media-box.aspect-1-1 .result-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.result-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

/* 提示词容器 */
.prompt-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.prompt-content {
  flex: 1;
  min-width: 0;
}

.prompt-container .prompt {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #e6e6e6;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 230, 172, 0.2);
}

.action-btn .icon {
  font-size: 16px;
}

.action-btn.delete {
  color: #aaa;
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

.action-btn.delete:hover {
  background: rgba(255, 71, 87, 0.1);
  border-color: rgba(255, 71, 87, 0.3);
  color: #ff4757;
  box-shadow: 0 2px 8px rgba(255, 71, 87, 0.2);
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #8892b0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 8px;
  color: #e6f1ff;
}

.empty-state p {
  font-size: 16px;
  max-width: 400px;
  margin: 0 auto;
}

/* 统一加载样式 - 与文案生成保持一致 */

/* 加载容器 */
.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  z-index: 2;
}

/* 玻璃效果 */
.glass-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 230, 172, 0.05) 0%,
    rgba(0, 230, 172, 0.1) 50%,
    rgba(0, 230, 172, 0.05) 100%
  );
  backdrop-filter: blur(10px);
  z-index: 1;
}

/* 玻璃反光动画 */
.glass-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  animation: shine 2s infinite;
  transform: rotate(45deg);
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}

/* 加载内容 */
.loading-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
}

/* 加载动画 - 旋转圆环 */
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 230, 172, 0.2);
  border-top-color: #00e6ac;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 加载文字 */
.loading-text {
  color: #00e6ac;
  font-size: 14px;
  font-weight: 500;
}

/* 进度条容器 */
.progress-bar-container {
  width: 100%;
  max-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 进度条 */
.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

/* 进度填充 */
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00d09d);
  border-radius: 3px;
  transition: width 0.2s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 进度文字 */
.progress-text {
  color: #8892b0;
  font-size: 14px;
  min-width: 40px;
  text-align: right;
}

/* 生成失败状态 */
.failed-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(13, 15, 22, 0.9);
  border-radius: 8px;
  gap: 12px;
  z-index: 2;
}

.failed-icon {
  font-size: 36px;
  opacity: 0.6;
}

.failed-text {
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 0 20px;
}

.retry-btn-small {
  padding: 6px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.2s;
}

.retry-btn-small:hover {
  transform: scale(1.05);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .image-list {
    padding: 0 20px;
    margin-bottom: 200px;
  }
  
  .image-item {
    padding: 16px;
    height: auto;
    min-height: auto;
    max-height: none;
  }
  
  .image-content-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .image-section {
    flex: none;
    width: 100%;
    height: 300px;
  }
  
  .prompt-container {
    flex-direction: column;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: center;
  }
}
</style>